import { categoryLabel } from "@/features/cost-allocations/utils/categories";
import type { ReceivedInvoiceLineRow } from "../api/receivedInvoicesApi";
import { formatInvoiceAmount } from "../utils/formatInvoiceAmount";

type Props = {
  lines: ReceivedInvoiceLineRow[];
  currency: string | null;
};

// Líneas del comprobante: tabla en desktop, cards apiladas en mobile (§9).
export function InvoiceLinesSection({ lines, currency }: Props) {
  if (lines.length === 0) return null;

  return (
    <section
      aria-label="Líneas de la factura"
      className="rounded-2xl border border-app-border bg-app-surface p-4"
    >
      <h2 className="font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em]">Líneas</h2>

      <div className="mt-3 hidden md:block">
        <table className="w-full text-left text-[13px]">
          <thead>
            <tr className="text-[11px] text-app-ink-3 uppercase tracking-wide">
              <th className="pr-3 pb-2 font-medium">CABYS</th>
              <th className="pr-3 pb-2 font-medium">Detalle</th>
              <th className="pr-3 pb-2 text-right font-medium">Cant.</th>
              <th className="pr-3 pb-2 text-right font-medium">Monto</th>
              <th className="pr-3 pb-2 text-right font-medium">IVA</th>
              <th className="pb-2 font-medium">Sugerencia</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id} className="border-app-border border-t">
                <td className="py-2 pr-3 font-mono text-[12px] text-app-ink-3">
                  {line.cabysCode ?? "—"}
                </td>
                <td className="py-2 pr-3 text-app-ink-1">
                  {line.detail}
                  {line.hasExoneration && <ExonerationBadge />}
                </td>
                <td className="py-2 pr-3 text-right text-app-ink-2 tabular-nums">
                  {formatQuantity(line.quantity)}
                </td>
                <td className="py-2 pr-3 text-right font-medium text-app-ink-1 tabular-nums">
                  {formatInvoiceAmount(line.lineTotalCents, currency)}
                </td>
                <td className="py-2 pr-3 text-right text-app-ink-2 tabular-nums">
                  {ivaCell(line, currency)}
                </td>
                <td className="py-2 text-app-ink-2">
                  {line.suggestedCategory ? categoryLabel(line.suggestedCategory) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="mt-3 flex flex-col gap-2 md:hidden">
        {lines.map((line) => (
          <li key={line.id} className="rounded-xl border border-app-border p-3">
            <div className="flex items-start justify-between gap-3">
              <span className="min-w-0 text-[13px] text-app-ink-1">
                {line.detail}
                {line.hasExoneration && <ExonerationBadge />}
              </span>
              <span className="shrink-0 font-medium text-[13px] text-app-ink-1 tabular-nums">
                {formatInvoiceAmount(line.lineTotalCents, currency)}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-app-ink-3">
              {line.cabysCode && <span className="font-mono">CABYS {line.cabysCode}</span>}
              <span>Cant. {formatQuantity(line.quantity)}</span>
              <span>IVA {ivaCell(line, currency)}</span>
              {line.suggestedCategory && (
                <span>Sugerencia: {categoryLabel(line.suggestedCategory)}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ExonerationBadge() {
  return (
    <span className="ml-1.5 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 font-medium text-[11px] text-amber-800">
      Exonerada
    </span>
  );
}

function formatQuantity(quantity: string | null): string {
  if (quantity === null) return "—";
  const num = Number(quantity);
  if (!Number.isFinite(num)) return quantity;
  // numeric(16,3) → sin ceros de relleno: "30.000" → "30", "2.500" → "2,5".
  return String(num).replace(".", ",");
}

// Monto de IVA + tarifa cuando viene (la tarifa 1% agro existe y importa).
function ivaCell(line: ReceivedInvoiceLineRow, currency: string | null): string {
  const amount = formatInvoiceAmount(line.taxCents, currency);
  if (line.ivaRate === null) return amount;
  const rate = Number(line.ivaRate);
  if (!Number.isFinite(rate)) return amount;
  return `${amount} (${String(rate).replace(".", ",")}%)`;
}
